import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { API } from "../App";
import { useAuth } from "../App";
import { useBootstrap, dispatchBrandChange } from "../providers/BootstrapProvider";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, Upload, RotateCcw, Save, Image as ImageIcon, Plus, Trash2, GripVertical, Download, History, RotateCw } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const LEGAL_META = [
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "terms", label: "Terms of Service" },
  { slug: "cookie", label: "Cookie Policy" },
  { slug: "disclaimer", label: "Disclaimer" },
];

const BrandLegalPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { refresh } = useBootstrap();

  const isFounder = (user?.email || "").toLowerCase() === "ramneetrdh@gmail.com";

  if (authLoading) return <div className="min-h-screen bg-[#FAF7F2]" />;
  if (!isFounder) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6">
        <Card className="max-w-md p-8 text-center" data-testid="brand-legal-forbidden">
          <h2 className="text-xl font-semibold text-[#6B4E8A] mb-2">Access restricted</h2>
          <p className="text-sm text-[#64748B]">Brand & Legal settings are editable only by the founder.</p>
          <Button variant="outline" className="mt-5" onClick={() => navigate("/admin")}>Back to Admin</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="brand-legal-page">
      <nav className="border-b border-[#E5DFEC] bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="text-[#64748B] hover:text-[#6B4E8A]"
              data-testid="brand-legal-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
            </Button>
          </div>
          <h1 className="text-base font-medium text-[#6B4E8A]" style={{ fontFamily: "Outfit, sans-serif" }}>
            Brand & Legal Settings
          </h1>
          <div className="w-24" />
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <p className="text-sm text-[#64748B] mb-6">
          Edit the platform logo, brand colours and all legal pages. Changes publish instantly and
          propagate across every page without redeploy. Visible only to the founder.
        </p>

        <Tabs defaultValue="logo" className="w-full">
          <TabsList className="bg-white border border-[#E5DFEC]">
            <TabsTrigger value="logo" data-testid="tab-logo">Logo</TabsTrigger>
            <TabsTrigger value="colors" data-testid="tab-colors">Colours</TabsTrigger>
            <TabsTrigger value="legal" data-testid="tab-legal">Legal Pages</TabsTrigger>
            <TabsTrigger value="popup" data-testid="tab-popup">Signup Popup</TabsTrigger>
          </TabsList>

          <TabsContent value="logo" className="mt-5">
            <LogoPanel onChanged={refresh} />
          </TabsContent>
          <TabsContent value="colors" className="mt-5">
            <ColorsPanel onChanged={refresh} />
          </TabsContent>
          <TabsContent value="legal" className="mt-5">
            <LegalPanel />
          </TabsContent>
          <TabsContent value="popup" className="mt-5">
            <SignupPopupPanel />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// ---------- Logo ----------

const LogoPanel = ({ onChanged }) => {
  const [brand, setBrand] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/brand`).then((r) => setBrand(r.data)).catch(() => {});
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be ≤5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
    uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(`${API}/admin/brand/logo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBrand(r.data);
      dispatchBrandChange({ logo_url: r.data.logo_url });
      onChanged?.();
      toast.success("Logo updated and live across the platform");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Logo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const revert = async () => {
    try {
      const r = await axios.delete(`${API}/admin/brand/logo`);
      setBrand(r.data);
      setPreview(null);
      dispatchBrandChange({ logo_url: r.data.logo_url });
      onChanged?.();
      toast.success("Logo reset to DHDH default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset logo");
    }
  };

  if (!brand) return <Card className="p-6">Loading…</Card>;

  const currentUrl = preview || brand.logo_url;

  return (
    <Card className="p-6 space-y-5" data-testid="logo-panel">
      <h3 className="text-base font-medium text-[#6B4E8A]">Platform logo</h3>
      <p className="text-sm text-[#64748B]">
        Appears in the top-left nav, landing pages, email headers, PDFs and the browser favicon.
        PNG, JPG, WEBP or SVG · max 5MB.
      </p>

      <div className="flex items-center gap-6">
        <div
          className="w-24 h-24 rounded-xl border border-[#E5DFEC] bg-white flex items-center justify-center overflow-hidden"
          data-testid="logo-preview"
        >
          {currentUrl ? (
            <img src={currentUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-8 h-8 text-[#C9BEDA]" />
          )}
        </div>
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
            data-testid="logo-file-input"
          />
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white"
              data-testid="logo-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading…" : "Upload new logo"}
            </Button>
            <Button
              variant="outline"
              onClick={revert}
              className="border-[#E5DFEC]"
              data-testid="logo-reset-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" /> Reset to default
            </Button>
          </div>
          <p className="text-xs text-[#8B7A99] mt-2 break-all">URL: {brand.logo_url}</p>
          {brand.updated_at && (
            <p className="text-xs text-[#8B7A99] mt-1">
              Last updated: {new Date(brand.updated_at).toLocaleString()}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

// ---------- Colours ----------

const ColorRow = ({ label, value, onChange, testid }) => (
  <div className="flex items-center gap-4">
    <div className="w-40 text-sm text-[#3F2A5A]">{label}</div>
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-16 h-10 rounded-md border border-[#E5DFEC] cursor-pointer"
      data-testid={`${testid}-picker`}
    />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-32 font-mono text-sm uppercase"
      maxLength={7}
      data-testid={`${testid}-hex`}
    />
    <div
      className="w-12 h-10 rounded-md border border-[#E5DFEC]"
      style={{ background: value }}
    />
  </div>
);

const ColorsPanel = ({ onChanged }) => {
  const [brand, setBrand] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/brand`).then((r) => {
      setBrand(r.data);
      setDraft({
        primary_color: r.data.primary_color,
        secondary_color: r.data.secondary_color,
        accent_color: r.data.accent_color,
      });
    });
  }, []);

  const update = (field, value) => setDraft((d) => ({ ...d, [field]: value }));

  const applyPreview = () => {
    // Push to BootstrapProvider so the whole app re-themes instantly WITHOUT writing to DB.
    dispatchBrandChange(draft);
    toast.success("Previewing — click Publish to save, Discard to revert");
  };

  const publish = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/brand/colors`, draft);
      setBrand(r.data);
      dispatchBrandChange(r.data);
      onChanged?.();
      toast.success("Colours published platform-wide");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save colours");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/brand/reset`);
      setBrand(r.data);
      setDraft({
        primary_color: r.data.primary_color,
        secondary_color: r.data.secondary_color,
        accent_color: r.data.accent_color,
      });
      dispatchBrandChange(r.data);
      onChanged?.();
      toast.success("Colours reset to DHDH defaults");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!brand) return;
    setDraft({
      primary_color: brand.primary_color,
      secondary_color: brand.secondary_color,
      accent_color: brand.accent_color,
    });
    dispatchBrandChange(brand);
    toast.success("Preview discarded");
  };

  if (!brand) return <Card className="p-6">Loading…</Card>;

  return (
    <Card className="p-6 space-y-6" data-testid="colors-panel">
      <h3 className="text-base font-medium text-[#6B4E8A]">Brand colours</h3>
      <p className="text-sm text-[#64748B]">
        Each colour maps to the matching palette (plum / cream / coral) across the entire platform.
        Preview before publishing — nothing goes live until you click Publish.
      </p>

      <div className="space-y-4">
        <ColorRow label="Primary (plum)" value={draft.primary_color} onChange={(v) => update("primary_color", v)} testid="color-primary" />
        <ColorRow label="Secondary (cream)" value={draft.secondary_color} onChange={(v) => update("secondary_color", v)} testid="color-secondary" />
        <ColorRow label="Accent (coral)" value={draft.accent_color} onChange={(v) => update("accent_color", v)} testid="color-accent" />
      </div>

      <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-[#E5DFEC]">
        <Button onClick={applyPreview} variant="outline" className="border-[#E5DFEC]" data-testid="colors-preview-btn">
          Preview
        </Button>
        <Button onClick={discard} variant="ghost" data-testid="colors-discard-btn">
          Discard preview
        </Button>
        <Button
          onClick={publish}
          disabled={saving}
          className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white"
          data-testid="colors-publish-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Publishing…" : "Publish"}
        </Button>
        <Button onClick={reset} variant="outline" className="ml-auto border-[#E5DFEC] text-[#B45309]" data-testid="colors-reset-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Reset to DHDH defaults
        </Button>
      </div>

      {brand.updated_at && (
        <p className="text-xs text-[#8B7A99]">
          Last published: {new Date(brand.updated_at).toLocaleString()}
        </p>
      )}
    </Card>
  );
};

// ---------- Legal ----------

const LegalPanel = () => {
  const [list, setList] = useState([]);
  const [active, setActive] = useState("privacy");
  const [doc, setDoc] = useState(null);
  const [html, setHtml] = useState("");
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/legal`).then((r) => setList(r.data || []));
  }, []);

  useEffect(() => {
    axios.get(`${API}/admin/legal/${active}`).then((r) => {
      setDoc(r.data);
      setHtml(r.data.html || "");
      setTitle(r.data.title || "");
    });
  }, [active]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/legal/${active}`, { title, html });
      setDoc(r.data);
      setList((prev) => prev.map((p) => (p.slug === active ? { ...p, title: r.data.title, updated_at: r.data.updated_at } : p)));
      toast.success(`${r.data.title} saved and live`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="legal-panel">
      <Card className="p-4">
        <div className="flex gap-2 flex-wrap">
          {LEGAL_META.map((m) => {
            const item = list.find((x) => x.slug === m.slug);
            const isActive = active === m.slug;
            return (
              <button
                key={m.slug}
                onClick={() => setActive(m.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition ${isActive ? "bg-[#6B4E8A] text-white" : "bg-[#F3EEF7] text-[#6B4E8A] hover:bg-[#E5D9EC]"}`}
                data-testid={`legal-tab-${m.slug}`}
              >
                {m.label}
                {item?.updated_at && (
                  <span className="ml-2 text-xs opacity-70">
                    {new Date(item.updated_at).toLocaleDateString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {doc && (
        <Card className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm text-[#3F2A5A] w-20">Title</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="flex-1"
              data-testid="legal-title-input"
            />
          </div>

          <div className="bg-white border border-[#E5DFEC] rounded-lg">
            <ReactQuill
              theme="snow"
              value={html}
              onChange={setHtml}
              modules={QUILL_MODULES}
              style={{ minHeight: 360 }}
            />
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-[#E5DFEC]">
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white"
              data-testid="legal-save-btn"
            >
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save & publish"}
            </Button>
            {doc.updated_at && (
              <span className="text-xs text-[#8B7A99]">
                Last updated: {new Date(doc.updated_at).toLocaleString()}
                {doc.updated_by && ` by ${doc.updated_by}`}
              </span>
            )}
            <a
              href={`/${active === "privacy" ? "privacy" : active === "terms" ? "terms" : active === "cookie" ? "cookie-policy" : "disclaimer"}`}
              target="_blank"
              rel="noreferrer"
              className="ml-auto text-xs text-[#6B4E8A] underline"
              data-testid="legal-view-public-link"
            >
              View public page →
            </a>
          </div>
        </Card>
      )}
    </div>
  );
};

export default BrandLegalPage;

// ---------- Signup Popup ----------

const SignupPopupPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/admin/agreement-popup/history?limit=50`);
      setHistory(r.data || []);
    } catch {
      // silent
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/admin/agreement-popup`).then((r) => setCfg(r.data));
    loadHistory();
  }, []);

  const downloadCsv = async () => {
    setExportBusy(true);
    try {
      const r = await axios.get(`${API}/admin/agreement-popup/export.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `dhdh-agreement-audit-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Audit CSV downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  const rollback = async (history_id) => {
    const ok = window.confirm(
      "Restore this version? The current config will be snapshotted into history first (nothing is lost)."
    );
    if (!ok) return;
    try {
      const r = await axios.post(`${API}/admin/agreement-popup/rollback/${history_id}`);
      setCfg(r.data);
      await loadHistory();
      toast.success("Popup restored to selected version");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };

  const update = (field, value) => setCfg((c) => ({ ...c, [field]: value }));

  const updateDoc = (idx, patch) =>
    setCfg((c) => ({
      ...c,
      documents: c.documents.map((d, i) => (i === idx ? { ...d, ...patch } : d)),
    }));

  const removeDoc = (idx) =>
    setCfg((c) => ({ ...c, documents: c.documents.filter((_, i) => i !== idx) }));

  const addDoc = () =>
    setCfg((c) => ({
      ...c,
      documents: [
        ...(c.documents || []),
        {
          key: `doc_${(c.documents || []).length + 1}`,
          title: "New Document",
          href: "/privacy",
          version: new Date().toISOString().slice(0, 10),
          enabled: true,
          beta_only: false,
        },
      ],
    }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/agreement-popup`, {
        heading: cfg.heading,
        intro_text: cfg.intro_text,
        agree_checkbox_label: cfg.agree_checkbox_label,
        verification_checkbox_label: cfg.verification_checkbox_label,
        agree_button_text: cfg.agree_button_text,
        decline_link_text: cfg.decline_link_text,
        show_beta_agreement: !!cfg.show_beta_agreement,
        documents: cfg.documents || [],
      });
      setCfg(r.data);
      toast.success("Signup popup saved — new members see this immediately");
      await loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save popup settings");
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <Card className="p-6">Loading…</Card>;

  return (
    <div className="space-y-5" data-testid="popup-panel">
      <Card className="p-5 space-y-4">
        <h3 className="text-base font-medium text-[#6B4E8A]">Content</h3>

        <div>
          <label className="text-xs text-[#8B7A99] uppercase tracking-wider">Heading</label>
          <Input value={cfg.heading} onChange={(e) => update("heading", e.target.value)} data-testid="popup-heading-input" />
        </div>
        <div>
          <label className="text-xs text-[#8B7A99] uppercase tracking-wider">Intro text</label>
          <Textarea value={cfg.intro_text} onChange={(e) => update("intro_text", e.target.value)} rows={3} data-testid="popup-intro-input" />
        </div>
        <div>
          <label className="text-xs text-[#8B7A99] uppercase tracking-wider">Agreement checkbox label</label>
          <Textarea value={cfg.agree_checkbox_label} onChange={(e) => update("agree_checkbox_label", e.target.value)} rows={2} data-testid="popup-agree-label-input" />
        </div>
        <div>
          <label className="text-xs text-[#8B7A99] uppercase tracking-wider">Verification checkbox label</label>
          <Textarea value={cfg.verification_checkbox_label} onChange={(e) => update("verification_checkbox_label", e.target.value)} rows={2} data-testid="popup-verify-label-input" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#8B7A99] uppercase tracking-wider">Agree button text</label>
            <Input value={cfg.agree_button_text} onChange={(e) => update("agree_button_text", e.target.value)} data-testid="popup-agree-btn-input" />
          </div>
          <div>
            <label className="text-xs text-[#8B7A99] uppercase tracking-wider">Decline link text</label>
            <Input value={cfg.decline_link_text} onChange={(e) => update("decline_link_text", e.target.value)} data-testid="popup-decline-input" />
          </div>
        </div>
        <div className="flex items-center gap-3 pt-2 border-t border-[#E5DFEC]">
          <Switch
            checked={!!cfg.show_beta_agreement}
            onCheckedChange={(v) => update("show_beta_agreement", !!v)}
            data-testid="popup-beta-toggle"
          />
          <div>
            <div className="text-sm font-medium text-[#3F2A5A]">Show Beta Tester Agreement</div>
            <div className="text-xs text-[#8B7A99]">Hides any documents flagged beta_only when off.</div>
          </div>
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-medium text-[#6B4E8A]">Linked documents</h3>
          <Button variant="outline" onClick={addDoc} className="border-[#E5DFEC]" data-testid="popup-add-doc-btn">
            <Plus className="w-4 h-4 mr-1" /> Add document
          </Button>
        </div>
        <p className="text-xs text-[#8B7A99]">
          Bump the version string on any document to force every existing member to re-accept
          it on their next page load. Toggle Beta-only to show a document only when the
          "Show Beta Tester Agreement" switch above is ON.
        </p>
        <div className="space-y-3">
          {(cfg.documents || []).map((d, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-[#FDF8F5] border border-[#E5DFEC] rounded-xl p-3" data-testid={`popup-doc-row-${idx}`}>
              <GripVertical className="w-4 h-4 text-[#C9BEDA] col-span-1" />
              <Input value={d.key} onChange={(e) => updateDoc(idx, { key: e.target.value })} className="col-span-2" placeholder="key" />
              <Input value={d.title} onChange={(e) => updateDoc(idx, { title: e.target.value })} className="col-span-3" placeholder="Title" />
              <Input value={d.href} onChange={(e) => updateDoc(idx, { href: e.target.value })} className="col-span-3" placeholder="/privacy" />
              <Input value={d.version} onChange={(e) => updateDoc(idx, { version: e.target.value })} className="col-span-2" placeholder="2026-06-25" />
              <div className="col-span-1 flex items-center gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => updateDoc(idx, { enabled: !d.enabled })}
                  className={`text-xs px-2 py-1 rounded-full ${d.enabled ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F1F5F9] text-[#64748B]"}`}
                  title={d.enabled ? "Enabled" : "Disabled"}
                >
                  {d.enabled ? "On" : "Off"}
                </button>
                <button
                  type="button"
                  onClick={() => updateDoc(idx, { beta_only: !d.beta_only })}
                  className={`text-xs px-2 py-1 rounded-full ${d.beta_only ? "bg-[#F3EEF7] text-[#6B4E8A]" : "bg-[#F1F5F9] text-[#64748B]"}`}
                  title="Beta-only"
                >
                  β
                </button>
                <button type="button" onClick={() => removeDoc(idx)} className="p-1 text-[#EF4444] hover:bg-[#FEE2E2] rounded" title="Remove">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#6B4E8A] hover:bg-[#553D6F] text-white"
          data-testid="popup-save-btn"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save & publish"}
        </Button>
        <Button
          onClick={downloadCsv}
          disabled={exportBusy}
          variant="outline"
          className="border-[#E5DFEC]"
          data-testid="popup-export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" /> {exportBusy ? "Exporting…" : "Download audit CSV"}
        </Button>
        {cfg.updated_at && (
          <span className="text-xs text-[#8B7A99]">
            Last saved: {new Date(cfg.updated_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Version history timeline */}
      <Card className="p-5 space-y-3" data-testid="popup-history-card">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-[#6B4E8A]" />
          <h3 className="text-base font-medium text-[#6B4E8A]">Version history</h3>
          <span className="text-xs text-[#8B7A99] ml-auto">
            {history.length === 0 ? "No prior saves yet" : `${history.length} previous ${history.length === 1 ? "save" : "saves"}`}
          </span>
        </div>
        {loadingHistory ? (
          <p className="text-sm text-[#8B7A99]">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-xs text-[#8B7A99]">Every time you save, we snapshot the previous version here so you can see what changed and roll back any time.</p>
        ) : (
          <ol className="space-y-2" data-testid="popup-history-list">
            {history.map((h, i) => (
              <li
                key={h.history_id}
                className="relative pl-6 pr-3 py-3 bg-[#FDF8F5] border border-[#E5DFEC] rounded-xl flex items-start gap-3"
                data-testid={`popup-history-entry-${i}`}
              >
                <span className="absolute left-2 top-5 w-2 h-2 rounded-full bg-[#6B4E8A]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#3F2A5A] truncate">
                    {h.heading || "(no heading)"}
                  </div>
                  <div className="text-xs text-[#64748B] mt-0.5">
                    {new Date(h.saved_at).toLocaleString()} · saved by {h.saved_by || "—"} · {h.doc_count} doc{h.doc_count === 1 ? "" : "s"}
                    {h.show_beta_agreement === false ? " · beta hidden" : ""}
                  </div>
                  {h.doc_versions && h.doc_versions.length > 0 && (
                    <div className="text-xs text-[#8B7A99] mt-1 font-mono truncate">
                      {h.doc_versions.map(d => `${d.key}@${d.version}${d.enabled === false ? "·off" : ""}`).join(" · ")}
                    </div>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => rollback(h.history_id)}
                  className="border-[#E5DFEC] shrink-0"
                  data-testid={`popup-history-rollback-${i}`}
                >
                  <RotateCw className="w-3.5 h-3.5 mr-1" /> Restore
                </Button>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
};
